import React, { Key, useEffect, useState } from 'react';
import { useDebounceFunc } from '../../../hooks/useDebounceFunc';
import styled from 'styled-components';
import { ScrollableList } from '../ScrollableList';
import Input from '../Input';
import ListItemSelection from '../ListItemSelection';
import { SearchOutlined } from '@ant-design/icons';
import Button from '../Button';
import { Loader } from '../Loader';
import { AllSelected } from '../../../../infrastructure/types/common/common-types';
import { arrayUtils } from '../../../../infrastructure/utils/array-utils';

interface Option<OptionIdType> {
  id: OptionIdType;
  title: string;
}

interface TableMultiSelectFilterProps<OptionIdType> {
  options: Option<OptionIdType>[];
  values: OptionIdType[] | [AllSelected];
  showSearch?: boolean;
  showAllSelected?: boolean;
  heightScrollableList?: `${number}px` | `${number}%`;
  onConfirm: (ids: OptionIdType[] | [AllSelected]) => void;
  onReset: () => void;
}

function MultiSelect<OptionIdType extends Key>({
  options,
  values,
  showSearch = false,
  showAllSelected = false,
  heightScrollableList = '160px',
  onConfirm,
  onReset,
}: TableMultiSelectFilterProps<OptionIdType>) {
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchPattern, setSearchPattern] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<OptionIdType[] | [AllSelected]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [filteredOptions, setFilteredOptions] = useState<Option<OptionIdType>[]>([]);
  const debouncedSearch = useDebounceFunc(400);

  useEffect(() => {
    if (searchPattern) {
      setLoading(true);
      debouncedSearch(() => filter());
    }
  }, [searchPattern]);

  useEffect(() => {
    if (selectedIds.length === options.length && showAllSelected) {
      setIsAllSelected(true);
    }
  }, [selectedIds.length]);

  useEffect(() => {
    setSelectedIds(values);
  }, [values]);

  const onCheck = (id: OptionIdType, checked: boolean) => {
    if (showAllSelected && isAllSelected && !checked) {
      setIsAllSelected(false);
      return setSelectedIds(options.filter(option => option.id !== id).map(item => item.id));
    }
    const result = [...selectedIds];
    arrayUtils.pushOrRemove(result as OptionIdType[], id);
    setSelectedIds(result as OptionIdType[]);
  };

  const filter = () => {
    if (options.length) {
      const result = options.filter(
        item => item.title.toLowerCase().indexOf(searchPattern.toLowerCase()) >= 0,
      );
      setFilteredOptions(result);
    }
    setLoading(false);
  };

  const isSelected = (id: OptionIdType | AllSelected) => {
    return (selectedIds as OptionIdType[]).find(selectedId => selectedId === id) !== undefined;
  };

  return (
    <DropdownWrapper>
      {showSearch && (
        <DropdownInput>
          <Input
            size={'middle'}
            prefix={<SearchOutlined />}
            value={searchPattern}
            onChange={value => setSearchPattern(value)}
          />
        </DropdownInput>
      )}
      <ScrollableList height={heightScrollableList}>
        {!!options.length && !searchPattern && showAllSelected && (
          <ListItemSelection
            key={'all'}
            checked={isAllSelected}
            title={'Выбрать все'}
            onChange={checked => {
              setSelectedIds(checked ? ['all'] : []);
              setIsAllSelected(checked);
            }}
          />
        )}
        {searchPattern ? (
          <>
            {!!filteredOptions.length &&
              filteredOptions.map(option => {
                return (
                  <ListItemSelection
                    key={option.id}
                    checked={isSelected(option.id) || isAllSelected}
                    title={option.title}
                    onChange={checked => onCheck(option.id, checked)}
                  />
                );
              })}
          </>
        ) : (
          <>
            {!!options.length &&
              options.map(option => {
                return (
                  <ListItemSelection
                    key={option.id}
                    checked={isSelected(option.id) || isAllSelected}
                    title={option.title}
                    onChange={checked => onCheck(option.id, checked)}
                  />
                );
              })}
          </>
        )}
        {loading && (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        )}
      </ScrollableList>
      <DropdownControls>
        <Button
          size={'small'}
          type={'text'}
          onClick={() => {
            setSelectedIds([]);
            setSearchPattern('');
            onReset();
          }}
        >
          Сбросить
        </Button>

        <Button
          size={'small'}
          type={'primary'}
          icon={<SearchOutlined />}
          onClick={() => onConfirm(selectedIds)}
        >
          Применить
        </Button>
      </DropdownControls>
    </DropdownWrapper>
  );
}

const DropdownWrapper = styled.div`
  min-width: 220px;
  padding: 12px;
`;

const DropdownControls = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`;

const DropdownInput = styled.div`
  margin-bottom: 12px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;
`;

export { MultiSelect as TableMultiSelectFilter };
